import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useDialog } from "../../../utils/hooks/use-dialog";

import type { UUID } from "../../../utils/uuid";
import type { EmailDesign } from "../server/supabase-email-design-service";
import type { FC } from "react";

export const EmailDesignSelector: FC<{
  value?: EmailDesign | undefined;
  emailDesigns: EmailDesign[];
  onChange: (x: { type: "new" } | { type: "existing"; id: UUID }) => void;
  showConfirmDialog: boolean;
  hasMaxTemplates: boolean;
  hideCreateNew?: boolean;
}> = ({ value, emailDesigns, onChange, showConfirmDialog, hasMaxTemplates, hideCreateNew }) => {
  const dialog = useDialog<{ type: "new" } | { type: "existing"; id: UUID }>();

  const { createNewOption, options } = useMemo(() => {
    const createNewOpt = { id: "new", name: "Create New", is_default: false };

    return {
      createNewOption: createNewOpt,
      options: hideCreateNew ? emailDesigns : [createNewOpt, ...emailDesigns],
    };
  }, [emailDesigns, hideCreateNew]);

  return (
    <>
      <Autocomplete
        value={value ?? (hideCreateNew ? undefined : createNewOption)}
        options={options}
        groupBy={
          hideCreateNew
            ? undefined
            : (option) => (option.id === "new" ? "Create New Template" : "Existing Templates")
        }
        getOptionLabel={(option) => `${option.name}${option.is_default ? " (Default)" : ""}`}
        getOptionDisabled={(option) => option.id === "new" && hasMaxTemplates}
        onChange={(_, val) =>
          (showConfirmDialog ? dialog.handleOpen : onChange)(
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            !val || val.id === "new" ? { type: "new" } : { type: "existing", id: val.id as UUID },
          )
        }
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Stack>
              {option.name}
              {option.is_default && " (Default)"}
              {hasMaxTemplates && option.id === "new" && (
                <Typography variant="caption" color="text.secondary">
                  You have reached your maximum number of templates. To increase your limit, contact
                  customer support.
                </Typography>
              )}
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Email Design" variant="outlined" data-lpignore />
        )}
      />
      {showConfirmDialog && (
        <Dialog open={dialog.open} onClose={dialog.handleClose}>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to change the selected Email Design? Any unsaved changes will be
              lost.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              data-analytics-id="email-design-selector-stay"
              variant="outlined"
              color="secondary"
              onClick={dialog.handleClose}
            >
              Stay
            </Button>
            <Button
              data-analytics-id="email-design-selector-proceed"
              variant="outlined"
              autoFocus
              onClick={() => {
                onChange(dialog.data ?? { type: "new" });
                dialog.handleClose();
              }}
            >
              Proceed
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
